import type { CharacterKind } from '@/components'
import type { AssetLibrary, ModelOptions } from './AssetLoader'

/**
 * Where each fighter's model comes from, if it has one.
 *
 * Empty means "use the procedural rig", which is what ships until real models
 * are in place. Filling a row in is the whole integration: the loader tints the
 * model to the player's colour, the collider and the kit are untouched, and a
 * model that fails to load simply falls back to the rig rather than breaking
 * the match.
 *
 * Paths are resolved against the site root, so a file at `public/models/x.glb`
 * is `/models/x.glb` here.
 */
/**
 * The roster, as modelled characters.
 *
 * All six are Quaternius **Universal Modular Characters**, CC0 1.0, from
 * poly.pizza. They are *people*: head roughly one seventh of the body, muted
 * cloth and leather, no snouts and no pastel. The entries before them were a
 * yeti, a frog, a bunny, a cactus, a demon and a dinosaur — free, rigged, and
 * completely wrong, picked because they happened to be what was already
 * downloaded rather than because they matched the brief, which from the first
 * message was stick figures in the spirit of TABS in a dark-fantasy setting.
 *
 * Chibi packs were rejected for a reason that only a frame shows: KayKit's
 * adventurers carry a head about a third of their height, and under this game's
 * 52° camera they read as a hat with feet. See docs/CHARACTERS.md.
 *
 * The six share **one skeleton of 62 bones and one vocabulary of 24 clips**
 * (`Idle`, `Walk`, `Run`, `Punch_Left/Right`, `Kick_Left/Right`, `Roll`,
 * `Sword_Slash`, `HitRecieve`, `HitRecieve_2`, `Death`, …), verified by
 * comparing every joint list and clip list against the first — 0 differences.
 * That is why one animation controller drives all of them, and why a seventh
 * fighter is a one-line change.
 *
 * `scale` is **measured, not guessed**: the vertices were run through their bone
 * transforms and the result fitted to the 2.6 m the renderer stands a fighter
 * at. Three's bounding box on a skinned mesh reports the *bind pose in local
 * units* — 0.018 m for these rigs, not 1.81 m — and trusting it scaled the first
 * imported fighter by a factor of a hundred and made it invisible.
 *
 * They rest on y = 0 (`min.y` within 4 mm of zero) and face +Z, the engine's own
 * forward, so `yOffset` and `yawOffset` are zero for all six.
 */
export const FIGHTER_MODELS: Partial<
  Record<CharacterKind, { url: string } & ModelOptions>
> = {
  // БРОНЯ — helmet and vest: the only closed silhouette in the six.
  guard: { url: `${import.meta.env.BASE_URL}models/guard.glb`, scale: 1.4326 },
  // ХВАТ — the mohawk. Reads at a glance from directly above, which is the
  // whole job of a silhouette in a game with a fixed overhead camera.
  beetle: { url: `${import.meta.env.BASE_URL}models/beetle.glb`, scale: 1.3461 },
  // ЕГЕРЬ — wide brim.
  crane: { url: `${import.meta.env.BASE_URL}models/crane.glb`, scale: 1.4297 },
  // ПРОРАБ — hard hat.
  brick: { url: `${import.meta.env.BASE_URL}models/brick.glb`, scale: 1.4215 },
  // ЗНАХАРЬ — hood.
  jester: { url: `${import.meta.env.BASE_URL}models/jester.glb`, scale: 1.4141 },
  // БРОДЯГА — bare head, so the one fighter with nothing on top is still a
  // distinct read rather than an absence.
  blob: { url: `${import.meta.env.BASE_URL}models/blob.glb`, scale: 1.4526 },
}

/**
 * Loads whatever is configured, in parallel, and never throws.
 *
 * A missing or broken model must not take the game down with it: the rig is
 * always there as a fallback, so the worst case of a bad path is that one
 * fighter looks the way they did before.
 */
export async function loadFighterModels(
  assets: AssetLibrary,
  /** Bytes received so far across all the models, as they arrive. */
  onProgress?: (loaded: number) => void,
): Promise<string[]> {
  const problems: string[] = []
  const entries = Object.entries(FIGHTER_MODELS) as Array<
    [CharacterKind, { url: string } & ModelOptions]
  >
  const received = new Map<CharacterKind, number>()

  await Promise.all(
    entries.map(async ([kind, spec]) => {
      try {
        const { url, ...options } = spec
        await assets.registerFighter(kind, url, options, (loaded) => {
          received.set(kind, loaded)
          let total = 0
          for (const bytes of received.values()) total += bytes
          onProgress?.(total)
        })
      } catch (error) {
        problems.push(`${kind}: ${String(error)}`)
      }
    }),
  )

  return problems
}
